import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkflowDashboard } from '../../components/workflow/WorkflowDashboard';
import { usePermissions } from '../../hooks/usePermissions';
import { useNotification } from '../../contexts/NotificationContext';
import { 
  WorkflowStatistics, 
  WorkflowFilters, 
  Product, 
  ProductWorkflowState, 
  User 
} from '../../types/pim';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { pimService } from '../../services/pimService';

export const WorkflowDashboardPage: React.FC = () => {
  const { t } = useTranslation();
  const { canReadProducts, canManagePim } = usePermissions();
  const { addNotification } = useNotification();
  
  const [statistics, setStatistics] = useState<WorkflowStatistics>({
    stateDistribution: [],
    priorityDistribution: [],
    averageProcessingTime: 0,
    overdueCount: 0
  });
  
  const [products, setProducts] = useState<Array<Product & { workflowState: ProductWorkflowState }>>([]);
  const [availableReviewers, setAvailableReviewers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<WorkflowFilters>({
    page: 1,
    limit: 50
  });

  useEffect(() => {
    if (canReadProducts) {
      loadWorkflowData();
      loadAvailableReviewers();
    }
  }, [canReadProducts, filters]);

  const loadWorkflowData = async () => {
    try {
      setLoading(true);
      
      // Load real workflow statistics from API using pimService
      const [statisticsResponse, productsResponse] = await Promise.all([
        pimService.getWorkflowDashboardStatistics(),
        pimService.getWorkflowProducts({
          page: filters.page || 1,
          limit: filters.limit || 50,
          ...(filters.state && { state: filters.state }),
          ...(filters.priority && { priority: filters.priority }),
          ...(filters.assignedTo && { assignedTo: filters.assignedTo }),
          ...(filters.overdue && { overdue: true })
        })
      ]);

      setStatistics(statisticsResponse.data || {
        stateDistribution: [],
        priorityDistribution: [],
        averageProcessingTime: 0,
        overdueCount: 0
      });
      setProducts(productsResponse.data?.products || []);
      
    } catch (error) {
      addNotification('error', t('pim.common.loadingError'));
      
      // Fallback to empty data
      setStatistics({
        stateDistribution: [],
        priorityDistribution: [],
        averageProcessingTime: 0,
        overdueCount: 0
      });
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const loadAvailableReviewers = async () => {
    try {
      const response = await pimService.getWorkflowReviewers();
      setAvailableReviewers(response.data || []);
    } catch (error) {
      setAvailableReviewers([]);
    }
  };

  const handleFilterChange = (newFilters: WorkflowFilters) => {
    setFilters(newFilters);
  };

  const handleViewProduct = (productId: number) => {
    // Navigate to product detail page
    window.location.href = `/pim/products/${productId}`;
  };

  const handleRefresh = () => {
    loadWorkflowData();
    loadAvailableReviewers();
  };

  if (!canReadProducts) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <ExclamationTriangleIcon className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-medium text-gray-900">{t('pim.common.accessDenied')}</h3>
          <p className="mt-1 text-sm text-gray-500">
            {t('pim.common.accessDeniedDescription')}
          </p>
        </div>
      </div>
    );
  }

  return (
    <WorkflowDashboard
      statistics={statistics}
      products={products}
      availableReviewers={availableReviewers}
      onFilterChange={handleFilterChange}
      onViewProduct={handleViewProduct}
      onRefresh={handleRefresh}
      isLoading={loading}
    />
  );
};

export default WorkflowDashboardPage;